import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import './Mapboxmap.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const BUILDINGS_LAYER_ID = 'sf-3d-buildings';
const DEM_SOURCE_ID      = 'mapbox-dem';

// ─────────────────────────────────────────────────────────────────────────
// Contrôle Mapbox custom — Réinitialiser la vue (centre/zoom/pitch/bearing)
// ─────────────────────────────────────────────────────────────────────────
class ResetViewControl {
  constructor(getInitialView) {
    this._getInitialView = getInitialView;
  }

  onAdd(map) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group sf-reset-ctrl';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sf-reset-ctrl__btn';
    button.setAttribute('aria-label', 'Réinitialiser la vue');
    button.title = 'Réinitialiser la vue';
    button.innerHTML = `
      <svg viewBox="0 0 20 20" width="18" height="18" class="sf-reset-ctrl__icon">
        <path
          d="M10 3.5V1L5.5 5 10 9V6.3c2.6 0 4.7 2.1 4.7 4.7s-2.1 4.7-4.7 4.7-4.7-2.1-4.7-4.7H3.5A6.5 6.5 0 1 0 10 4.5V3.5z"
          fill="currentColor"
          stroke="currentColor"
          stroke-width="0.6"
          stroke-linejoin="round"
        />
      </svg>
    `;

    button.addEventListener('click', () => {
      const view = this._getInitialView?.();
      if (!view || !this._map) return;
      button.classList.add('sf-reset-ctrl__btn--active');
      this._map.easeTo({
        center: view.center,
        zoom: view.zoom,
        pitch: view.pitch,
        bearing: view.bearing,
        duration: 1100,
      });
      setTimeout(() => button.classList.remove('sf-reset-ctrl__btn--active'), 750);
    });

    this._container.appendChild(button);
    return this._container;
  }

  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = undefined;
  }
}

const MapboxMap = forwardRef(({
  style     = 'mapbox://styles/mapbox/satellite-streets-v12',
  center    = [2.3514, 46.8566],
  zoom      = 5,
  className = '',
  onLoad,
}, ref) => {
  const containerRef   = useRef(null);
  const mapInstanceRef = useRef(null);

  const terrainActiveRef     = useRef(false);
  const buildings3DActiveRef = useRef(false);

  // Vue initiale figée à la création — sert de référence pour le reset
  const initialViewRef = useRef({
    center,
    zoom,
    pitch:   0,
    bearing: 0,
  });

  useImperativeHandle(ref, () => ({
    getMap:       () => mapInstanceRef.current,
    flyTo:        (options)    => mapInstanceRef.current?.flyTo(options),
    addLayer:     (layer)      => mapInstanceRef.current?.addLayer(layer),
    addSource:    (id, source) => mapInstanceRef.current?.addSource(id, source),
    getSource:    (id)         => mapInstanceRef.current?.getSource(id),
    removeLayer:  (id) => { if (mapInstanceRef.current?.getLayer(id))  mapInstanceRef.current.removeLayer(id);  },
    removeSource: (id) => { if (mapInstanceRef.current?.getSource(id)) mapInstanceRef.current.removeSource(id); },

    // ── Réinitialiser la vue à l'état initial ───────────────────────────
    resetView: () => {
      const map  = mapInstanceRef.current;
      const view = initialViewRef.current;
      if (!map || !view) return;
      map.easeTo({
        center: view.center,
        zoom: view.zoom,
        pitch: view.pitch,
        bearing: view.bearing,
        duration: 1100,
      });
    },

    // ── Relief 3D (terrain) ─────────────────────────────────────────────
    setTerrain: (enabled, exaggeration = 1.5) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      try {
        if (enabled) {
          if (!map.getSource(DEM_SOURCE_ID)) {
            map.addSource(DEM_SOURCE_ID, {
              type: 'raster-dem',
              url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
              tileSize: 512,
              maxzoom: 14,
            });
          }
          map.setTerrain({ source: DEM_SOURCE_ID, exaggeration });
          if (map.getPitch() < 40) {
            map.easeTo({ pitch: 60, duration: 900 });
          }
        } else {
          map.setTerrain(null);
          if (!buildings3DActiveRef.current) {
            map.easeTo({ pitch: 0, bearing: 0, duration: 900 });
          }
        }
        terrainActiveRef.current = enabled;
      } catch (e) {
        console.warn('setTerrain:', e);
      }
    },

    // ── Bâtiments 3D (fill-extrusion) ───────────────────────────────────
    setBuildings3D: (enabled) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      try {
        if (enabled) {
          if (!map.getLayer(BUILDINGS_LAYER_ID)) {
            const layers = map.getStyle()?.layers || [];
            const labelLayer = layers.find(
              (l) => l.type === 'symbol' && l.layout?.['text-field']
            );
            map.addLayer(
              {
                id: BUILDINGS_LAYER_ID,
                source: 'composite',
                'source-layer': 'building',
                filter: ['==', 'extrude', 'true'],
                type: 'fill-extrusion',
                minzoom: 14,
                paint: {
                  'fill-extrusion-color': '#2d3748',
                  'fill-extrusion-height': [
                    'interpolate', ['linear'], ['zoom'],
                    14, 0,
                    14.5, ['get', 'height'],
                  ],
                  'fill-extrusion-base': [
                    'interpolate', ['linear'], ['zoom'],
                    14, 0,
                    14.5, ['get', 'min_height'],
                  ],
                  'fill-extrusion-opacity': 0.75,
                },
              },
              labelLayer?.id
            );
          }
          if (map.getPitch() < 40) {
            map.easeTo({ pitch: 55, duration: 900 });
          }
        } else {
          if (map.getLayer(BUILDINGS_LAYER_ID)) map.removeLayer(BUILDINGS_LAYER_ID);
          if (!terrainActiveRef.current) {
            map.easeTo({ pitch: 0, bearing: 0, duration: 900 });
          }
        }
        buildings3DActiveRef.current = enabled;
      } catch (e) {
        console.warn('setBuildings3D:', e);
      }
    },
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style,
      center,
      zoom,
      attributionControl: false,
    });

    // Geocoder — haut gauche
    map.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        placeholder: 'Rechercher un lieu…',
        marker: { color: '#be1622' },
      }),
      'top-left'
    );

    // Navigation (zoom +/–, rotation) — bas droite
    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

    // Géolocalisation — bas droite
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions:   { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading:   true,
      }),
      'bottom-right'
    );

    // Réinitialiser la vue — bas droite, juste sous la géolocalisation
    map.addControl(
      new ResetViewControl(() => initialViewRef.current),
      'bottom-right'
    );

    // Échelle — bas gauche
    map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');

    map.on('load', () => {
      mapInstanceRef.current = map;

      if (!map.getSource(DEM_SOURCE_ID)) {
        map.addSource(DEM_SOURCE_ID, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        });
      }

      onLoad?.(map);
    });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []); // eslint-disable-line
  useEffect(() => {
    const handleResize = () => {
      mapInstanceRef.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <div
      ref={containerRef}
      className={`mapbox-map-container ${className}`}
    />
  );
});

MapboxMap.displayName = 'MapboxMap';
export default MapboxMap;